import { type RngFn, createRng, boxMullerNormal } from '../shared/rng.util';
import type { DataGeneratorConfig } from './descriptive-generator.model';

/**
 * Gamma variate using Marsaglia & Tsang's method.
 * For alpha < 1, uses the Ahrens-Dieter boost.
 */
function gammaVariate(alpha: number, rng: RngFn): number {
  if (alpha < 1) {
    return gammaVariate(alpha + 1, rng) * Math.pow(rng(), 1 / alpha);
  }

  const d = alpha - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    let x: number;
    let v: number;
    do {
      x = boxMullerNormal(0, 1, rng);
      v = 1 + c * x;
    } while (v <= 0);

    v = v * v * v;
    const u = rng();
    if (u < 1 - 0.0331 * x * x * x * x) {
      return d * v;
    }
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) {
      return d * v;
    }
  }
}

function betaVariate(alpha: number, beta: number, rng: RngFn): number {
  const x = gammaVariate(alpha, rng);
  const y = gammaVariate(beta, rng);
  return x / (x + y);
}

// =============================================================================
// Distribution generators
// =============================================================================

function generateUniform(n: number, min: number, max: number, rng: RngFn): number[] {
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    result.push(min + rng() * (max - min));
  }
  return result;
}

function generateNormal(n: number, mean: number, sd: number, rng: RngFn): number[] {
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    result.push(boxMullerNormal(mean, sd, rng));
  }
  return result;
}

function generateRightSkewed(n: number, min: number, max: number, rng: RngFn): number[] {
  const range = max - min;
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    result.push(min + betaVariate(2, 5, rng) * range);
  }
  return result;
}

function generateLeftSkewed(n: number, min: number, max: number, rng: RngFn): number[] {
  const range = max - min;
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    result.push(min + betaVariate(5, 2, rng) * range);
  }
  return result;
}

function generateBimodal(n: number, center1: number, center2: number, spread: number, rng: RngFn): number[] {
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    const m = rng() < 0.5 ? center1 : center2;
    result.push(boxMullerNormal(m, spread, rng));
  }
  return result;
}

/**
 * Exponential decay using the inverse CDF method: X = -ln(U) / λ.
 * Most values clustered near min, long right tail toward max.
 */
function generateExpDecay(n: number, min: number, max: number, rng: RngFn): number[] {
  const range = max - min;
  const lambda = 4 / range;
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    let u: number;
    do {
      u = rng();
    } while (u === 0);
    result.push(min + -Math.log(u) / lambda);
  }
  return result;
}

/**
 * Exponential growth — mirror of decay.
 * Most values clustered near max, long left tail toward min.
 */
function generateExpGrowth(n: number, min: number, max: number, rng: RngFn): number[] {
  const range = max - min;
  const lambda = 4 / range;
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    let u: number;
    do {
      u = rng();
    } while (u === 0);
    result.push(max - -Math.log(u) / lambda);
  }
  return result;
}

/**
 * U-shaped distribution using Beta(0.5, 0.5) — the arcsine distribution.
 * Most values clustered at both extremes (near min and max), few in the middle.
 */
function generateUShaped(n: number, min: number, max: number, rng: RngFn): number[] {
  const range = max - min;
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    result.push(min + betaVariate(0.5, 0.5, rng) * range);
  }
  return result;
}

// =============================================================================
// Post-processing
// =============================================================================

function clampValues(values: number[], min: number, max: number): number[] {
  return values.map((v) => Math.min(max, Math.max(min, v)));
}

function applyDecimalPlaces(values: number[], dp: number): number[] {
  return values.map((v) => Number(v.toFixed(dp)));
}

function applyIntegerRounding(values: number[]): number[] {
  return values.map((v) => Math.round(v));
}

/**
 * Compute a percentile from a sorted array using linear interpolation.
 */
function percentile(sorted: number[], p: number): number {
  const n = sorted.length;
  if (n === 0) {
    return 0;
  }
  if (n === 1) {
    return sorted[0];
  }
  const rank = (p / 100) * (n - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  const frac = rank - lo;
  return sorted[lo] * (1 - frac) + sorted[hi] * frac;
}

/**
 * Inject outliers using Tukey's IQR fence method.
 *
 * An outlier is a value beyond Q1 − 1.5·IQR (low) or Q3 + 1.5·IQR (high).
 * Each outlier is placed randomly between 1.5·IQR and 3·IQR beyond the fence,
 * which covers mild-to-extreme outlier territory without being absurd.
 *
 * If IQR ≈ 0 (all values nearly identical), falls back to 10% of the data range.
 * Outliers are appended (not replacing existing data points).
 */
function injectOutliers(values: number[], count: number, _min: number, _max: number, rng: RngFn): number[] {
  if (count <= 0 || values.length === 0) {
    return values;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const q1 = percentile(sorted, 25);
  const q3 = percentile(sorted, 75);
  let iqr = q3 - q1;

  // Fallback for near-zero IQR (e.g. uniform with very few points)
  if (iqr < 1e-9) {
    const dataRange = sorted[sorted.length - 1] - sorted[0];
    iqr = Math.max(dataRange * 0.1, 1);
  }

  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;

  const result = [...values];

  for (let i = 0; i < count; i++) {
    // Alternate low/high, with randomness for odd counts
    const isLow = count === 1 ? rng() < 0.5 : i % 2 === 0;

    // Random distance between 1.5·IQR and 3·IQR beyond the fence
    const extraDistance = iqr * (1.5 + rng() * 1.5);

    let outlier: number;
    if (isLow) {
      outlier = lowerFence - extraDistance;
    } else {
      outlier = upperFence + extraDistance;
    }

    result.push(outlier);
  }

  return result;
}

// =============================================================================
// Public API
// =============================================================================

/** Generators that take (n, param1, param2, rng) — param1/param2 meaning depends on distribution */
const GENERATORS: Record<string, (n: number, p1: number, p2: number, rng: RngFn) => number[]> = {
  uniform: generateUniform,
  normal: generateNormal,
  'right-skewed': generateRightSkewed,
  'left-skewed': generateLeftSkewed,
  'exp-decay': generateExpDecay,
  'exp-growth': generateExpGrowth,
  'u-shaped': generateUShaped,
};

import { BIMODAL_DISTRIBUTIONS, MEAN_SD_DISTRIBUTIONS } from './descriptive-generator.model';

export function generateData(config: DataGeneratorConfig): number[] {
  const { distribution, sampleSize, outliers, decimalPlaces, seed, integerOnly } = config;
  const rng = createRng(seed);
  const usesMeanSd = MEAN_SD_DISTRIBUTIONS.has(distribution);
  const isBimodal = BIMODAL_DISTRIBUTIONS.has(distribution);

  let values: number[];

  if (isBimodal) {
    values = generateBimodal(sampleSize, config.center1, config.center2, config.spread, rng);
  } else {
    const generator = GENERATORS[distribution];
    const p1 = usesMeanSd ? config.mean : config.minValue;
    const p2 = usesMeanSd ? config.stdDev : config.maxValue;
    values = generator(sampleSize, p1, p2, rng);
  }

  // Clamp to range (only for min/max distributions)
  if (!usesMeanSd && !isBimodal) {
    values = clampValues(values, config.minValue, config.maxValue);
  }

  // Inject outliers
  if (outliers > 0) {
    values = injectOutliers(values, outliers, config.minValue, config.maxValue, rng);
  }

  // Apply formatting
  if (integerOnly) {
    values = applyIntegerRounding(values);
  } else {
    values = applyDecimalPlaces(values, decimalPlaces);
  }

  return values;
}
